import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { passwordSchema } from '@firegame/shared';
import { resetPassword } from '../api/auth.js';
import { ApiClientError } from '../api/client.js';
import { AuthCard } from '../ui/AuthCard.js';
import { Button } from '../ui/Button.js';
import { Field } from '../ui/Field.js';
import { t } from '../i18n/index.js';

export function ResetPasswordPage() {
  const labels = t();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('jeton') ?? '';

  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setFieldErrors({});

    if (password !== passwordConfirm) {
      setFieldErrors({ passwordConfirm: labels.auth.register.passwordMismatch });
      return;
    }

    const parsed = passwordSchema.safeParse(password);
    if (!parsed.success) {
      setFieldErrors({ password: parsed.error.issues[0]?.message ?? labels.errors.unexpected });
      return;
    }

    setBusy(true);
    try {
      await resetPassword({ token, password });
      setDone(true);
    } catch (cause) {
      setError(cause instanceof ApiClientError ? cause.message : labels.errors.unexpected);
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <AuthCard title={labels.auth.reset.title}>
        <div className="fg-alert fg-alert--error" role="alert">
          {labels.auth.reset.missingToken}
        </div>
        <Link to="/mot-de-passe-oublie">{labels.auth.forgot.title}</Link>
      </AuthCard>
    );
  }

  if (done) {
    return (
      <AuthCard title={labels.auth.reset.title}>
        <div className="fg-alert fg-alert--success" role="status">
          {labels.auth.reset.success}
        </div>
        <Link to="/connexion">{labels.auth.reset.goToLogin}</Link>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={labels.auth.reset.title} subtitle={labels.auth.reset.subtitle}>
      <form onSubmit={handleSubmit} noValidate>
        {error && (
          <div className="fg-alert fg-alert--error" role="alert">
            {error}
          </div>
        )}

        <Field
          label={labels.auth.newPasswordLabel}
          type="password"
          name="password"
          autoComplete="new-password"
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          error={fieldErrors.password}
          hint={labels.auth.passwordHint}
          disabled={busy}
        />

        <Field
          label={labels.auth.passwordConfirmLabel}
          type="password"
          name="passwordConfirm"
          autoComplete="new-password"
          required
          value={passwordConfirm}
          onChange={(event) => setPasswordConfirm(event.target.value)}
          error={fieldErrors.passwordConfirm}
          disabled={busy}
        />

        <div className="fg-auth__actions">
          <Button type="submit" busy={busy}>
            {labels.auth.reset.submit}
          </Button>
        </div>
      </form>
    </AuthCard>
  );
}
